import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface DomainConfig {
  id: string;
  name: string;
  labelName: string;
  description: string;
  route: string;
  order: number;
  /** Hex. Border/accent color for the domain (used by tag pills + sidebar). */
  outlineColor?: string;
  /** Hex. Fill color for the domain's tag pill background. */
  fillColor?: string;
  /** Cognito groups required to see this domain. Empty/undefined = visible to all. */
  requiredGroups?: string[];
  /** Slack channel ID linked to this domain for notifications. */
  slackChannelId?: string;
  /** Cached Slack channel name for display. */
  slackChannelName?: string;
  /** Slack canvas linked to this domain (created in the linked channel). */
  slackCanvasId?: string;
  /** ISO timestamp of the last canvas sync. */
  slackCanvasUpdatedAt?: string;
  /** Optional container id this tag belongs to. Container records themselves have no containerId. */
  containerId?: string | null;
}

export type DomainConfigMap = Record<string, DomainConfig>;

/**
 * Resolve a domain id to the set of LEAF domain ids whose tagged items belong to
 * it. A container expands to its child leaves (domains whose `containerId` points
 * back at it); a leaf (or an unknown id) resolves to just itself. Config-driven
 * so domain pages/widgets aggregate the right leaves without any hardcoded map.
 */
export function leavesForDomain(id: string, all: DomainConfig[]): string[] {
  const children = all.filter((d) => d.containerId === id).map((d) => d.id);
  return children.length ? children : [id];
}

const QUERY_KEY = ['domains'];

interface DomainsResponse {
  data: { domains: DomainConfigMap };
}

export function useDomains() {
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<DomainsResponse>('/system/domains');
      return response.data.domains;
    },
    staleTime: 5 * 60 * 1000,
  });

  const domains = query.data ?? {};
  const list = Object.values(domains).sort((a, b) => a.order - b.order);
  // Only leaf domains (those nested under a container) are taggable. Containers
  // are nav groupings and must never be put on an item as a tag, so every domain
  // picker keys off this list. Driven by the live config's `containerId` rather
  // than a hardcoded set so it tracks taxonomy edits automatically.
  const taggableList = list.filter((d) => !!d.containerId);

  // STRICT leaves: a domain that has a parent AND is nobody else's parent.
  // An umbrella domain can have a containerId (so it passes the taggable
  // filter) while still being the parent of other domains — those should not
  // be offered as tag targets. We exclude any domain whose id shows up as
  // another domain's containerId.
  const parentIds = new Set(
    list.map((d) => d.containerId).filter((id): id is string => !!id),
  );
  const leafList = list.filter((d) => !!d.containerId && !parentIds.has(d.id));

  return {
    domains,
    list,
    taggableList,
    leafList,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useDomain(id: string | undefined) {
  const { domains, isLoading } = useDomains();
  return {
    domain: id ? domains[id] : undefined,
    isLoading,
  };
}

export function useDomainByRoute(route: string) {
  const { list, isLoading } = useDomains();
  const clean = route.startsWith('/') ? route : `/${route}`;
  return {
    domain: list.find((d) => d.route === clean),
    isLoading,
  };
}

export function useUpdateDomains() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domains: DomainConfigMap) => {
      const response = await api.put<DomainsResponse>('/system/domains', { domains });
      return response.data.domains;
    },
    onSuccess: (domains) => {
      queryClient.setQueryData(QUERY_KEY, domains);
    },
  });
}
